import React, { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import type { BaseWidget } from '@/types/chatbot.types';
import { renderWidget } from './widgetRegistry';
import * as S from './widgets.styles';

interface DynamicAIResponseProps {
  widgets: BaseWidget[];
  shouldAnimate?: boolean;
  isStreaming?: boolean;
  isLiveMessage?: boolean;
  onPublishClick?: (insightId: string) => void;
}

const createWidgetKey = (widget: BaseWidget, index: number): string => {
  if (widget.type === 'Text' && widget.params?.content) {
    const content = widget.params.content;
    if (typeof content === 'string') {
      return `text-${index}-${content.length}-${content.substring(0, 30).replace(/\s/g, '')}`;
    }
  }
  return `${widget.type}-${index}`;
};

const DynamicAIResponse: React.FC<DynamicAIResponseProps> = observer(
  ({ widgets, shouldAnimate = true, isStreaming = false, isLiveMessage = false, onPublishClick }) => {
    const previousWidgetCountRef = useRef<number>(0);
    const previousIsStreamingRef = useRef<boolean>(isStreaming);
    const widgetsRef = useRef<BaseWidget[]>(widgets);

    const [visibleWidgetIndex, setVisibleWidgetIndex] = useState<number>(() =>
      isLiveMessage && !isStreaming && shouldAnimate ? 0 : widgets.length,
    );
    const [completedWidgets, setCompletedWidgets] = useState<Set<number>>(() =>
      isLiveMessage && !isStreaming && shouldAnimate
        ? new Set<number>()
        : new Set(Array.from({ length: widgets.length }, (_, i) => i)),
    );

    useEffect(() => {
      if (!isLiveMessage) {
        setVisibleWidgetIndex(widgets.length);
        setCompletedWidgets(new Set(Array.from({ length: widgets.length }, (_, i) => i)));
        previousIsStreamingRef.current = false;
        previousWidgetCountRef.current = 0;
        return;
      }

      const wasStreaming = previousIsStreamingRef.current;
      const isCompleting = wasStreaming && !isStreaming && shouldAnimate;

      if (isStreaming) {
        previousWidgetCountRef.current = widgets.length;
        setVisibleWidgetIndex(widgets.length);
        setCompletedWidgets(new Set(Array.from({ length: widgets.length }, (_, i) => i)));
        widgetsRef.current = widgets;
        previousIsStreamingRef.current = true;
        return;
      }

      if (!shouldAnimate) {
        setVisibleWidgetIndex(widgets.length);
        setCompletedWidgets(new Set(Array.from({ length: widgets.length }, (_, i) => i)));
        widgetsRef.current = widgets;
        previousIsStreamingRef.current = false;
        return;
      }

      if (isCompleting) {
        const startIndex = previousWidgetCountRef.current;
        if (widgets.length > startIndex) {
          setVisibleWidgetIndex(startIndex);
          setCompletedWidgets(new Set(Array.from({ length: startIndex }, (_, i) => i)));
        } else {
          setVisibleWidgetIndex(widgets.length);
          setCompletedWidgets(new Set(Array.from({ length: widgets.length }, (_, i) => i)));
        }
        widgetsRef.current = widgets;
        previousIsStreamingRef.current = false;
        return;
      }

      if (widgets !== widgetsRef.current) {
        widgetsRef.current = widgets;
        if (isLiveMessage && !isStreaming && shouldAnimate) {
          setVisibleWidgetIndex(0);
          setCompletedWidgets(new Set<number>());
        } else {
          setVisibleWidgetIndex(widgets.length);
          setCompletedWidgets(new Set(Array.from({ length: widgets.length }, (_, i) => i)));
        }
      }

      previousIsStreamingRef.current = isStreaming;
    }, [widgets, shouldAnimate, isStreaming, isLiveMessage]);

    const handleWidgetComplete = (index: number) => {
      setCompletedWidgets(prev => {
        const newSet = new Set(prev);
        newSet.add(index);
        return newSet;
      });

      if (index < widgets.length - 1) {
        setVisibleWidgetIndex(index + 1);
      }
    };

    const shouldWidgetAnimate = (index: number): boolean => {
      if (!isLiveMessage || index > visibleWidgetIndex) return false;
      if (index === 0) return true;

      for (let i = 0; i < index; i++) {
        if (!completedWidgets.has(i)) return false;
      }
      return true;
    };

    if (!widgets || widgets.length === 0) {
      return null;
    }

    return (
      <S.DynamicAIResponseContainer>
        {widgets.map((widget, index) => {
          const widgetKey = createWidgetKey(widget, index);
          const isVisible = isStreaming || !shouldAnimate || index <= visibleWidgetIndex;

          if (!isVisible) {
            return null;
          }

          const widgetShouldAnimate = isLiveMessage && shouldAnimate && shouldWidgetAnimate(index);

          return (
            <React.Fragment key={widgetKey}>
              {renderWidget(
                widget,
                index,
                widgetShouldAnimate,
                () => handleWidgetComplete(index),
                isStreaming,
                onPublishClick,
              )}
            </React.Fragment>
          );
        })}

        {isStreaming && (
          <S.StreamingIndicator>
            <span>...</span>
          </S.StreamingIndicator>
        )}
      </S.DynamicAIResponseContainer>
    );
  },
);

DynamicAIResponse.displayName = 'DynamicAIResponse';

export default DynamicAIResponse;
